import { ArrowDown, Printer } from "lucide-react";
import { LogoMark } from "@/components/brand/LogoMark";
import { Container, KeyValues, RunningHead } from "@/components/paper/paper";
import { PrismScene } from "@/components/scenes/PrismScene";
import { Button } from "@/components/ui/button";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	CONTACT_EMAIL,
	CV_PDF_URL,
	GITHUB_URL,
	LINKEDIN_URL,
} from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import {
	ContactSection,
	JourneySection,
	MissionsSection,
	ProductsSection,
	ProfileSection,
	SkillsSection,
} from "./sections/Sections";

const COMPACT = "py-8 md:py-10";

/** Web version of the PDF resume, same content and order, with the PDF one click away. */
export function CvDocument() {
	const { t, dict } = useI18n();
	const { capture } = useAnalytics();
	const header = dict.cv.header;
	const c = dict.home.contacts;

	return (
		<div className="pb-6">
			<Container className="flex flex-wrap items-center justify-between gap-4 pt-10 print:hidden">
				<p className="text-kicker">{t((t) => t.cv.pageLabel)}</p>
				<div className="flex flex-wrap gap-3">
					<Button asChild>
						<a
							href={CV_PDF_URL}
							download
							onClick={() =>
								capture(ANALYTICS_EVENTS.ctaClick, {
									cta: "cv_pdf",
									placement: "cv_toolbar",
									href: CV_PDF_URL,
								})
							}
						>
							{t((t) => t.cv.download)}
							<ArrowDown aria-hidden="true" />
						</a>
					</Button>
					<Button variant="outline" onClick={() => window.print()}>
						{t((t) => t.cv.print)}
						<Printer aria-hidden="true" />
					</Button>
				</div>
			</Container>

			<article className="mx-auto mt-6 w-full max-w-[60rem] border border-rule-2 bg-paper/60 shadow-[0_18px_40px_-28px_rgba(42,42,48,0.45)] print:mt-0 print:max-w-none print:border-0 print:shadow-none">
				<Container className="pt-8 md:pt-10">
					<RunningHead
						left={
							<span className="inline-flex items-center gap-2">
								<LogoMark className="size-4" />
								simon_boisset.cv
							</span>
						}
						right={header.location}
					/>
					<div className="mt-8 grid gap-8 md:grid-cols-[1fr_15.5rem] md:gap-10">
						<div>
							<p
								className="print-in font-dots text-[clamp(2.6rem,7vw,4.6rem)] leading-[0.92] font-extrabold text-ink"
								aria-hidden="true"
							>
								{header.name}
							</p>
							<h1 className="mt-5 font-mono text-[clamp(1.1rem,2.2vw,1.5rem)] font-medium text-ink">
								<span className="sr-only">{header.name}, </span>
								{t((t) => t.home.hero.titleLead)}{" "}
								<span className="pastel-mark whitespace-nowrap">
									{t((t) => t.home.hero.titleMark)}
								</span>
							</h1>
							<p className="mt-5 leading-[1.7] text-ink-2">{header.intro}</p>
						</div>
						<KeyValues
							className="self-end border-l border-dashed border-rule-2 pl-5"
							items={[
								{
									key: c.email,
									value: CONTACT_EMAIL,
									href: `mailto:${CONTACT_EMAIL}`,
								},
								{ key: c.site, value: "simonboisset.com" },
								{
									key: c.linkedin,
									value: "in/simon-boisset",
									href: LINKEDIN_URL,
									external: true,
								},
								{
									key: c.github,
									value: "simonboisset",
									href: GITHUB_URL,
									external: true,
								},
								{ key: c.location, value: header.location },
							]}
						/>
					</div>
					<PrismScene className="mt-10" />
				</Container>

				<ProfileSection n="01" className={COMPACT} />
				<JourneySection n="02" className={COMPACT} />
				<SkillsSection n="03" className={COMPACT} />
				<MissionsSection n="04" className={COMPACT} />
				<ProductsSection n="05" className={COMPACT} />
				<ContactSection n="06" className={COMPACT} />
			</article>
		</div>
	);
}
